import { ClientOrg, Copyright, Hymn, OrgHymnal, SongList } from '../../../common/src/model.ts';
import { isBound } from '../../../common/src/model_util.ts';
import { parseSongSlug } from '../../../common/src/paths.ts';
import { getCanonicalHymnalSlug, HymnalWithHymns } from '../data/hymnals.ts';

// A song's copyright issues are waived when the household has access to it through
// this week's song lists, a primary hymnal, a singing plan, or the org hymnal.
// They are also waived for internal users, who may view and play every song regardless of
// copyright status so they can QA the engraving and audio before it is cleared (issue #3451).
// The Library list does not use this function: it keeps its greyed-out styling for internal
// users so they can still tell which songs are copyrighted.
export function getCopyrightIssues({
  hymn,
  isInternalUser,
  songsForWeek,
  householdOrgs,
  hymnals,
  orgHymnal,
}: {
  hymn: Hymn | undefined;
  isInternalUser: boolean;
  songsForWeek: SongList[];
  householdOrgs: ClientOrg[];
  hymnals: HymnalWithHymns[];
  orgHymnal: OrgHymnal;
}): Copyright[] | undefined {
  if (!hymn || !hymn.copyrights || !hymn.slug) {
    return;
  }
  if (isInternalUser) {
    return;
  }
  if (songsForWeek.some((list) => list.songs.some((entry) => isBound(entry) && entry.slug === hymn.slug))) {
    return;
  }
  const hymnalSlug = parseSongSlug(hymn.slug).hymnalSlug;
  const primaryHymnals =
    householdOrgs.flatMap(({ primaryHymnalIds }) =>
      primaryHymnalIds?.map((id) => hymnals.find((hymnal) => hymnal.id === id)),
    ) ?? [];
  // a primary hymnal may be an alternate title whose songs carry the canonical slug (issue #3454)
  if (primaryHymnals.some((hymnal) => hymnal && getCanonicalHymnalSlug(hymnal) === hymnalSlug)) {
    return;
  }
  if (
    householdOrgs.some(({ singingPlan }) =>
      singingPlan?.days.some((day) => day.songs.some((song) => song.slug === hymn.slug)),
    )
  ) {
    return;
  }
  if (orgHymnal.songs.some((song) => song.slug === hymn.slug)) {
    return;
  }
  return hymn.copyrights;
}
